import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Trophy, Users, Calendar, ShoppingBag, Award, Clock } from 'lucide-react';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Home = () => {
  const [schedules, setSchedules] = useState([]);

  useEffect(() => {
    fetchSchedules();
  }, []);

  const fetchSchedules = async () => {
    try {
      const response = await axios.get(`${API}/schedules`);
      setSchedules(response.data);
    } catch (error) {
      console.error('Error fetching schedules:', error);
    }
  };

  const daysOfWeek = [
    { key: 'monday', label: 'Segunda' },
    { key: 'tuesday', label: 'Terça' },
    { key: 'wednesday', label: 'Quarta' },
    { key: 'thursday', label: 'Quinta' },
    { key: 'friday', label: 'Sexta' },
    { key: 'saturday', label: 'Sábado' },
    { key: 'sunday', label: 'Domingo' }
  ];

  const getSchedulesForDay = (day) => {
    return schedules.filter(s => s.day_of_week === day).sort((a, b) => {
      const timeA = a.time_slot.split('-')[0];
      const timeB = b.time_slot.split('-')[0];
      return timeA.localeCompare(timeB);
    });
  };

  const allTimeSlots = [...new Set(schedules.map(s => s.time_slot))].sort();

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      {/* Hero Section with Video */}
      <section className="relative h-[600px] bg-black overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <iframe
            src="https://player.vimeo.com/video/165441754?autoplay=1&loop=1&muted=1&background=1"
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              width: '177.78vh',  /* 16/9 * 100vh */
              height: '56.25vw', /* 9/16 * 100vw */
              minWidth: '100%',
              minHeight: '100%',
              transform: 'translate(-50%, -50%)',
              border: 'none',
            }}
            allow="autoplay; fullscreen"
            title="Pequeninos do Jockey"
          />
        </div>
        <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
          <div className="text-center text-white px-4">
            <h1 className="text-5xl md:text-6xl font-bold mb-6" data-testid="hero-title">
              Pequeninos do Jockey
            </h1>
            <p className="text-2xl md:text-3xl mb-8">
              Transformando crianças em grandes seres humanos
            </p>
            <Link to="/enrollment">
              <Button size="lg" className="bg-red-600 hover:bg-red-700 text-lg px-8 py-6" data-testid="enroll-button">
                Faça sua Inscrição
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Sobre Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Sobre o Pequeninos do Jockey</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Com mais de 45 anos de tradição na formação de jogadores de futebol, somos uma associação desportiva 
              sem fins econômicos, focada na inclusão social e excelência esportiva.
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mt-12">
            <Card className="text-center border-red-600">
              <CardContent className="pt-6">
                <Award className="w-12 h-12 text-red-600 mx-auto mb-3" />
                <h3 className="text-3xl font-bold text-gray-900">45+</h3>
                <p className="text-gray-600">Anos de História</p>
              </CardContent>
            </Card>
            <Card className="text-center border-red-600">
              <CardContent className="pt-6">
                <Users className="w-12 h-12 text-red-600 mx-auto mb-3" />
                <h3 className="text-3xl font-bold text-gray-900">1000+</h3>
                <p className="text-gray-600">Atletas Formados</p>
              </CardContent>
            </Card>
            <Card className="text-center border-red-600">
              <CardContent className="pt-6">
                <Trophy className="w-12 h-12 text-red-600 mx-auto mb-3" />
                <h3 className="text-3xl font-bold text-gray-900">50+</h3>
                <p className="text-gray-600">Títulos Conquistados</p>
              </CardContent>
            </Card>
            <Card className="text-center border-red-600">
              <CardContent className="pt-6">
                <Calendar className="w-12 h-12 text-red-600 mx-auto mb-3" />
                <h3 className="text-3xl font-bold text-gray-900">20+</h3>
                <p className="text-gray-600">Campeonatos Internacionais</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Categorias Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Nossas Categorias</h2>
            <p className="text-xl text-gray-600">
              Oferecemos treinamento especializado para todas as idades
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { name: 'Sub-7 e Sub-9', desc: 'Iniciação e desenvolvimento motor', ages: '5 a 9 anos' },
              { name: 'Sub-11 e Sub-13', desc: 'Aprimoramento técnico e tático', ages: '10 a 13 anos' },
              { name: 'Sub-15 e Sub-17', desc: 'Preparação profissional', ages: '14 a 17 anos' }
            ].map((cat, idx) => (
              <Card key={idx} className="hover:shadow-lg transition">
                <CardContent className="p-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{cat.name}</h3>
                  <p className="text-red-600 font-semibold mb-3">{cat.ages}</p>
                  <p className="text-gray-600 mb-4">{cat.desc}</p>
                  <Link to="/categories">
                    <Button variant="outline" className="w-full border-red-600 text-red-600 hover:bg-red-600 hover:text-white">
                      Ver Detalhes
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Horários Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Horários de Treinos</h2>
            <p className="text-xl text-gray-600">
              Confira nossa grade de horários semanal
            </p>
          </div>

          {schedules.length > 0 ? (
            <div className="mx-auto max-w-6xl">
              <div className="overflow-x-auto bg-white shadow-2xl rounded-xl border border-gray-200">
                <table className="w-full border-collapse min-w-[800px]">
                  <thead>
                    <tr className="bg-gradient-to-r from-red-600 to-red-700 text-white">
                      <th className="p-4 text-left font-bold border-r border-red-500 text-sm md:text-base w-32">
                        <Clock className="inline mr-2" size={18} />
                        Horário
                      </th>
                      {daysOfWeek.map(day => (
                        <th key={day.key} className="p-4 text-center font-bold border-r border-red-500 last:border-r-0 text-xs md:text-sm">
                          {day.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {allTimeSlots.map((timeSlot, index) => (
                      <tr key={timeSlot} className={`${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'} hover:bg-red-50 transition`}>
                        <td className="p-3 font-semibold border-r border-gray-300 text-gray-700 text-xs md:text-sm whitespace-nowrap">
                          <Clock className="inline mr-2 text-red-600" size={16} />
                          {timeSlot}
                        </td>
                        {daysOfWeek.map(day => {
                          const daySchedules = getSchedulesForDay(day.key).filter(s => s.time_slot === timeSlot);
                          return (
                            <td key={day.key} className="p-2 border-r border-gray-200 last:border-r-0 align-top min-w-[100px]">
                              {daySchedules.map(schedule => (
                                <div key={schedule.id} className="mb-2 p-2 bg-gradient-to-br from-red-100 to-red-50 rounded-lg hover:from-red-200 hover:to-red-100 transition border border-red-200 text-xs md:text-sm">
                                  <div className="font-semibold text-red-800 leading-tight">
                                    {schedule.categories.join(', ')}
                                  </div>
                                  {schedule.notes && (
                                    <div className="text-xs text-gray-600 mt-1">
                                      {schedule.notes}
                                    </div>
                                  )}
                                </div>
                              ))}
                              {daySchedules.length === 0 && (
                                <div className="text-center text-gray-300 text-xs">-</div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <Clock className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Horários em breve</p>
            </div>
          )}

          <div className="text-center mt-8">
            <Link to="/schedules">
              <Button className="bg-red-600 hover:bg-red-700 text-lg px-8 py-6 shadow-lg hover:shadow-xl transition">
                Ver Grade Completa
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-red-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">Venha Fazer Parte da Nossa História!</h2>
          <p className="text-xl mb-8">
            Inscreva seu filho e faça parte do time que revela os grandes craques do futebol brasileiro.
          </p>
          <Link to="/enrollment">
            <Button size="lg" className="bg-white text-red-600 hover:bg-gray-100 text-lg px-8 py-6" data-testid="cta-enroll-button">
              Inscrever Agora
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;